import { activeRegionAtom } from "@/atoms/activeRegion";
import AuthGate from "@/components/Auth/AuthGate";
import AuthPromptDialog from "@/components/Auth/AuthPromptDialog";
import Layout from "@/components/Layout";
import ReportCard from "@/components/SurfFeed/ReportCard";
import ReportFormDialog from "@/components/SurfFeed/ReportFormDialog";
import { useAuth } from "@/hooks/useAuth";
import {
  useRegion,
  useZones,
  getCamerasFromRegion,
  findRegionInZones,
} from "@/hooks/useRegions";
import { useRegionReports } from "@/lib/reports";
import { SurfReport } from "@/types/surfReport";
import EditNoteIcon from "@mui/icons-material/EditNote";
import PeopleIcon from "@mui/icons-material/People";
import { Box, Button, Container, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useRecoilValue } from "recoil";

export default function FeedPage() {
  const activeRegion = useRecoilValue(activeRegionAtom);
  const { data: regionData } = useRegion(activeRegion);
  const { data: zones } = useZones();
  const found = zones ? findRegionInZones(zones, activeRegion) : null;
  const { user } = useAuth();
  const { data: reports = [], isLoading: reportsLoading } =
    useRegionReports(activeRegion);
  const cameras = useMemo(
    () => (regionData ? getCamerasFromRegion(regionData) : []),
    [regionData],
  );
  const defaultCamera = cameras.length > 0 ? cameras[0] : null;

  const [formOpen, setFormOpen] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<SurfReport | null>(null);

  const handleReportClick = () => {
    if (!user) {
      setAuthPromptOpen(true);
      return;
    }
    setFormOpen(true);
  };

  const handleEdit = (report: SurfReport) => {
    setEditingReport(report);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingReport(null);
  };

  const regionLabel = found?.region.label || activeRegion;

  return (
    <Layout>
      <AuthGate
        source="feed"
        title="See what's happening in the water."
        reason="Sign in to read surf reports from the community and post your own."
      >
        <Container maxWidth="sm" sx={{ py: { xs: 2, md: 3 } }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <PeopleIcon sx={{ fontSize: 22, color: "accent.main" }} />
              <Box>
                <Typography variant="h4" sx={{ lineHeight: 1.15, color: "text.primary" }}>
                  Live Reports
                </Typography>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", lineHeight: 1.2 }}
                >
                  {regionLabel} — {reports.length}{" "}
                  {reports.length === 1 ? "report" : "reports"} active
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Report CTA */}
          <Button
            variant="contained"
            fullWidth
            startIcon={<EditNoteIcon />}
            onClick={handleReportClick}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              py: 1.25,
              mb: 2,
              fontSize: "0.85rem",
            }}
          >
            Report Conditions
          </Button>

          {/* Full feed */}
          {reports.length === 0 && !reportsLoading ? (
            <Box
              sx={{
                py: 6,
                textAlign: "center",
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography sx={{ fontSize: "0.9rem", color: "text.primary" }}>
                No reports yet for {regionLabel}.
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Be the first to share conditions.
              </Typography>
            </Box>
          ) : reports.length === 0 ? null : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {reports.map((report) => (
                <ReportCard
                  key={report.id}
                  report={report}
                  onAuthRequired={() => setAuthPromptOpen(true)}
                  onEdit={handleEdit}
                />
              ))}
            </Box>
          )}

          {/* Dialogs */}
          <ReportFormDialog
            open={formOpen}
            onClose={handleFormClose}
            regionId={activeRegion}
            regionLabel={regionLabel}
            camera={defaultCamera}
            editingReport={editingReport}
          />
          <AuthPromptDialog
            open={authPromptOpen}
            onClose={() => setAuthPromptOpen(false)}
          />
        </Container>
      </AuthGate>
    </Layout>
  );
}
